import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { disableHiddenStandardControls, isStandardFieldHidden } from "../../standard-field-visibility.helper";
import { ToastService } from "@core/components/toasts/toasts.service";
import { FormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EmployeeService } from "services/corporate/employee.service";
import { DomainService } from "services/domain.service";
import { ApprovalScopeActionType } from "services/enums/approvalScopeActionType.enum";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { EmployeeDismissalTypeModel } from "services/models/employee-dismissal-type.model";
import { OptionModel } from "services/models/model";
import { WorkforcePlanTeamEmployeeModel } from "services/models/workforceplan";
import { WorkforcePlanDrawerEmployeeService } from "services/workforceplan/workforceplan-employee.service";
import { formattedPhone, guidEmpty } from "utils/helps";
import {
	DrawerDataInput,
	FormRequestDismissalPayload,
	FormRequestDismissalValues,
	ISolicitationFormComponent,
} from "../../form-types";

type DismissalLists = {
	resignationTypesOptions?: OptionModel[];
	nextMovementTypesOptions?: OptionModel[];
	dismissalTypeOptions?: EmployeeDismissalTypeModel[];
};

@Component({
	selector: "app-dismissal-form",
	templateUrl: "./dismissal-form.component.html",
	styleUrls: ["./dismissal-form.component.scss"],
})
export class DismissalFormComponent implements OnChanges, OnDestroy, ISolicitationFormComponent {
	@Input() data: DrawerDataInput | null = null;
	@Input() source: "action-required" | null = null;
	@Input() hiddenStandardFields: string[] = [];
	@Output() employeeLoadingChange = new EventEmitter<boolean>();

	form: UntypedFormGroup;
	lists: DismissalLists = {};
	isInitializingForm = false;
	isLoadingResignationTypes = false;
	isLoadingDismissalTypes = false;
	private destroy$ = new Subject<void>();

	constructor(
		private _formBuilder: FormBuilder,
		private _drawerEmployeeService: WorkforcePlanDrawerEmployeeService,
		private _employeeService: EmployeeService,
		private _toastService: ToastService,
		private _domainService: DomainService,
	) {
		this.form = this._formBuilder.group({
			note: [null, Validators.required],
			employeeId: [this.data?.employee?.id],
			movementType: [WorkforceplanMovementType.DISMISSAL.id],
			effectiveDate: [],
			workScaleGroup: [this.data?.workScaleGroup],
			workforcePlanId: [this.data?.workforcePlanId],
			resignationReason: [null, Validators.required],
			dismissalType: [this._normalizeDismissalType(null), Validators.required],
			wasEmployeeInitiative: [null, Validators.required],
			expectedDate: [null, Validators.required],
			vacancyReplacement: [null],
		});
	}

	public isHidden(fieldKey: string): boolean {
		return isStandardFieldHidden(this.hiddenStandardFields, fieldKey);
	}

	ngOnChanges(changes: SimpleChanges): void {
		const dataChanged = !!changes["data"]?.currentValue;
		if (dataChanged && this.data?.type === "REQUEST_DISMISSAL") {
			void this._initializeForm();
		}

		if (changes["hiddenStandardFields"] && this.form && !dataChanged) {
			this.applyHiddenStandardFields();
		}
	}

	private applyHiddenStandardFields(): void {
		disableHiddenStandardControls(this.form, this.hiddenStandardFields);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	getForm(): UntypedFormGroup {
		return this.form;
	}

	getPayload(): FormRequestDismissalPayload {
		const {
			note,
			employeeId,
			movementType,
			effectiveDate,
			workScaleGroup,
			workforcePlanId,
			resignationReason,
			dismissalType,
			wasEmployeeInitiative,
			vacancyReplacement,
			expectedDate,
		} = this.form.getRawValue() as FormRequestDismissalValues;

		return {
			note,
			employeeId,
			movementType,
			effectiveDate: effectiveDate ?? undefined,
			workScaleGroup,
			workforcePlanId,
			payload: JSON.stringify({
				resignationReason,
				wasEmployeeInitiative,
				expectedDate,
				dismissalType: Number(dismissalType),
				vacancyReplacement,
			}),
		};
	}

	getUpdatePayload(): string {
		const { expectedDate } = this.form.getRawValue() as FormRequestDismissalValues;

		return JSON.stringify({ expectedDate });
	}

	get resignationReasonDescription(): string {
		const selectedValue = this.form.get("resignationReason")?.value;
		if (!this.lists.resignationTypesOptions) return "";
		const option = this.lists.resignationTypesOptions.find((item) => item.label === selectedValue);
		return option?.description ?? "Motivo de desligamento";
	}

	get isLoadingDismissalFields(): boolean {
		return this.isInitializingForm || this.isLoadingResignationTypes || this.isLoadingDismissalTypes;
	}

	private async _initializeForm(): Promise<void> {
		this.isInitializingForm = true;

		try {
			await this.ensureEmployeeLoaded();

			if (this.data?.mode === "details") {
				this._buildDetailsForm();
			} else {
				this._buildAddForm();
			}

			await Promise.all([this._loadNextMovementTypes(), this._loadResignationTypes(), this._loadDismissalTypes()]);
		} finally {
			this.applyHiddenStandardFields();
			this.isInitializingForm = false;
		}
	}

	private _buildAddForm(): void {
		let payload: any = {};

		try {
			const jsonObj = this.data?.solicitationDetails?.payload;
			payload = jsonObj ? JSON.parse(jsonObj) : {};
		} catch {
			payload = {};
		}

		this.form = this._formBuilder.group({
			note: [null, Validators.required],
			employeeId: [this.data?.employee?.id],
			movementType: [WorkforceplanMovementType.DISMISSAL.id],
			effectiveDate: [],
			workScaleGroup: [this.data?.workScaleGroup],
			workforcePlanId: [this.data?.workforcePlanId],
			resignationReason: [payload.resignationReason ?? null, Validators.required],
			dismissalType: [this._normalizeDismissalType(payload.dismissalType), Validators.required],
			wasEmployeeInitiative: [payload.wasEmployeeInitiative ?? false, Validators.required],
			expectedDate: [payload.expectedDate ?? null, Validators.required],
			vacancyReplacement: [this._normalizeVacancyReplacement(payload.vacancyReplacement)],
		});
	}

	private _buildDetailsForm(): void {
		const solicitation = this.data?.solicitation;
		const solicitationDetails = this.data?.solicitationDetails;
		const payloadData = solicitationDetails?.payloadData;

		this.form = this._formBuilder.group({
			note: [solicitationDetails?.note],
			employeeId: [solicitation?.employeeId],
			movementType: [solicitation?.movementTypeData?.id ?? WorkforceplanMovementType.DISMISSAL.id],
			effectiveDate: [solicitation?.effectiveDate?.substring(0, 10)],
			workScaleGroup: [solicitation?.workScaleGroup ?? this.data?.workScaleGroup],
			workforcePlanId: [this.data?.workforcePlanId],
			resignationReason: [payloadData?.resignationReason],
			dismissalType: [this._normalizeDismissalType(payloadData?.dismissalType)],
			wasEmployeeInitiative: [payloadData?.wasEmployeeInitiative],
			expectedDate: [payloadData?.expectedDate?.substring(0, 10)],
			vacancyReplacement: [this._normalizeVacancyReplacement(payloadData?.vacancyReplacement)],
		});

		if (!solicitationDetails?.permission?.canUpdate) {
			this.form.disable();
		}

		if (this._canEditExpectedDismissalDate()) {
			this.form.get("expectedDate")?.enable();
		}
	}

	private async _loadResignationTypes(): Promise<void> {
		this.isLoadingResignationTypes = true;

		try {
			const data = await this._drawerEmployeeService
				.getListResignationTypes()
				.pipe(takeUntil(this.destroy$))
				.toPromise();

			this.lists.resignationTypesOptions = data ?? [];
		} finally {
			this.isLoadingResignationTypes = false;
		}
	}

	private async _loadDismissalTypes(): Promise<void> {
		const companyId = this.data?.employee?.companyId;
		if (!companyId) {
			this.lists.dismissalTypeOptions = [];
			return;
		}

		this.isLoadingDismissalTypes = true;

		try {
			const data = await this._employeeService
				.getListDismissalTypes(companyId)
				.pipe(takeUntil(this.destroy$))
				.toPromise();

			this.lists.dismissalTypeOptions = data ?? [];
		} finally {
			this.isLoadingDismissalTypes = false;
		}
	}

	private async _loadNextMovementTypes(): Promise<void> {
		const options = [
			WorkforceplanMovementType.TRAINNING,
			WorkforceplanMovementType.COST_CENTER_AND_JOB_TITLE_CHANGE,
			WorkforceplanMovementType.NEW_HIRING_SUBSTITUTION,
		];

		const hideTraining = this.data?.mode !== "details" && (await this._isExemptFromTrainingValidation());

		this.lists.nextMovementTypesOptions = options
			.filter((item) => !hideTraining || item !== WorkforceplanMovementType.TRAINNING)
			.map((item) => ({ label: item.label, value: item.value }) as OptionModel);
	}

	private async _isExemptFromTrainingValidation(): Promise<boolean> {
		try {
			const settings = await this._domainService
				.getCompanyGroupSettings()
				.pipe(takeUntil(this.destroy$))
				.toPromise();
			const parameters = settings?.workforcePlanParameters ? JSON.parse(settings.workforcePlanParameters) : {};
			const companyIds: string[] =
				parameters?.ExemptTrainingValidationCompanyIds ?? parameters?.exemptTrainingValidationCompanyIds ?? [];
			const jobTitleGroupIds: string[] =
				parameters?.ExemptTrainingValidationJobTitleGroupIds ?? parameters?.exemptTrainingValidationJobTitleGroupIds ?? [];
			const companyId = (this.data?.employee?.companyId ?? this.data?.companyId ?? "").toLowerCase();
			const jobTitleGroupId = (this.data?.employee?.jobTitleGroupId ?? "").toLowerCase();

			return (
				(!!companyId && companyIds.some((id) => id?.toLowerCase() === companyId)) ||
				(!!jobTitleGroupId && jobTitleGroupIds.some((id) => id?.toLowerCase() === jobTitleGroupId))
			);
		} catch {
			return false;
		}
	}

	private _normalizeVacancyReplacement(value: unknown): string | null {
		if (value === null || value === undefined || value === "") {
			return null;
		}

		return WorkforceplanMovementType.getByAny(value)?.value ?? null;
	}

	private _normalizeDismissalType(value: unknown): number | null {
		if (value === null || value === undefined || value === "") {
			return null;
		}

		const numericValue = Number(value);
		return Number.isNaN(numericValue) ? null : numericValue;
	}

	private _canEditExpectedDismissalDate(): boolean {
		return !!this.data?.solicitationDetails?.hasStepActionType(
			ApprovalScopeActionType.EDIT_EXPECTED_DISMISSAL_DATE.value
		);
	}

	private _isEmployeePartial(): boolean {
		const employee = this.data?.employee as WorkforcePlanTeamEmployeeModel & { timeShiftTitle?: string };

		if (!employee?.id || employee.id === guidEmpty) {
			return false;
		}

		return (
			!employee.registration ||
			!employee.costCenterName ||
			!employee.jobTitleName ||
			(!employee.timeShiftTitle && !employee.shiftSchedule)
		);
	}

	private async ensureEmployeeLoaded(): Promise<void> {
		if (!this.data) return;

		if ((!this.data.employee?.id || this.data.employee.id === guidEmpty) && this.data.solicitation?.employeeId) {
			this.data.employee = {
				...this.data.employee,
				id: this.data.solicitation.employeeId,
			} as WorkforcePlanTeamEmployeeModel;
		}

		if (!this._isEmployeePartial()) return;

		await this._loadEmployeeData();
	}

	private async _loadEmployeeData(): Promise<void> {
		if (!this.data?.employee?.id || this.data.employee.id === guidEmpty) return;

		this.employeeLoadingChange.emit(true);

		try {
			const employee = Object.assign({}, this.data.employee) as WorkforcePlanTeamEmployeeModel & { timeShiftTitle?: string };
			const { data } = await this._employeeService.get(employee.id, true).toPromise();

			employee.companyId = data.companyId;
			employee.admissionDate = data.admissionDate;
			employee.cellPhoneNumber = formattedPhone(data.cellPhoneNumber);
			employee.profileImagePath = data.profileImagePath;
			employee.timeShiftId = data.timeShiftId;
			employee.timeShiftTitle = data.timeShiftTitle ?? employee.timeShiftTitle ?? employee.shiftSchedule;
			employee.centerCostId = data.costCenterId;
			employee.costCenterName = data.costCenterName;
			employee.jobTitleId = data.jobTitleId;
			employee.jobTitleName = data.jobTitleName;
			employee.tradeUnionId = data.tradeUnionId;
			employee.registration = data.registration;

			this.data.employee = {
				...employee,
			} as WorkforcePlanTeamEmployeeModel;
		} catch {
			this._toastService.showError("Não foi possível carregar os dados do colaborador.");
		} finally {
			this.employeeLoadingChange.emit(false);
		}
	}
}
